import {
    Alert,
    AlertIcon,
    Spinner,
} from '@chakra-ui/react';
import React, { useState } from 'react';
import FormField from '../../../components/fields/FormField';
import FormModal from '../../../components/FormModal';
import FormStack from '../../../components/form-stack';
import InlineErrorState from '../../../components/states/InlineErrorState';
import InlineScopePrompt from '../../_shared/InlineScopePrompt';
import { translate } from '../../../i18n';
import { GameDto } from '../../../models/game/dto/game.dto';
import { useGetCourts } from '../../court-management/hooks/useGetCourts';
import { isUnreadable } from '../../analytics/utils/statValue';
import { useSelectedLocation } from '../../location-management/hooks/useSelectedLocation';
import { useUpdateGameCourt } from '../hooks/useUpdateGameCourt';

interface EditGameCourtModalProps {
    isOpen: boolean;
    onClose: () => void;
    game: GameDto;
}

export const EditGameCourtModal: React.FC<EditGameCourtModalProps> = ({
    isOpen,
    onClose,
    game
}) => {
    const [selectedCourtId, setSelectedCourtId] = useState<number>(game.court.id);
    const { selectedLocation } = useSelectedLocation();
    const { data: courts, isError: isCourtsError, state: courtsReadState } = useGetCourts(selectedLocation?.id);
    const updateGameCourtMutation = useUpdateGameCourt(game.id);

    const handleSave = async () => {
        if (selectedCourtId === game.court.id) {
            onClose();
            return;
        }

        try {
            await updateGameCourtMutation.mutateAsync({ courtId: selectedCourtId });
            onClose();
        } catch (error) {
            // Error will be handled by the mutation's error state
        }
    };

    const handleClose = () => {
        setSelectedCourtId(game.court.id); // Reset to original court
        onClose();
    };

    return (
        <FormModal
            isOpen={isOpen}
            onClose={handleClose}
            title={translate('game.edit_court')}
            onSubmit={handleSave}
            submitLabel={translate('save')}
            cancelLabel={translate('cancel')}
            isSubmitting={updateGameCourtMutation.isLoading}
            isSubmitDisabled={!selectedCourtId || selectedCourtId === game.court.id}
        >
            <FormStack>
                {updateGameCourtMutation.error && (
                    <Alert status="error">
                        <AlertIcon />
                        {updateGameCourtMutation.error instanceof Error
                            ? updateGameCourtMutation.error.message
                            : translate('game.error_updating_court')}
                    </Alert>
                )}

                <FormField
                    type="select"
                    label={translate('game.current_court')}
                    value={game.court.id.toString()}
                    onChange={() => {}}
                    isDisabled
                    options={[
                        {
                            value: game.court.id.toString(),
                            label: `${game.court.name} (${translate('game.current')})`,
                        },
                    ]}
                />

                {/*
                    `courtsReadState` rather than the raw `isLoading` (D62): run
                    134 gated `useGetCourts` on `locationId`, and React Query v4
                    reports a disabled query as `isLoading: true` with
                    `fetchStatus: 'idle'` — so the old flag would have left this
                    spinner turning forever whenever no club was selected, which
                    D64 rates strictly worse than the empty destination list it
                    replaced. `never-issued` gets the prompt instead: there is
                    nowhere to move this game to until a club is chosen.
                */}
                {courtsReadState === 'loading' ? (
                    <Spinner size="sm" />
                ) : courtsReadState === 'never-issued' ? (
                    <InlineScopePrompt gate="location" />
                ) : isUnreadable(isCourtsError, courts) ? (
                    // `courts ?? []` used to render an empty destination list,
                    // which reads as "there is nowhere to move this game to"
                    // (R89-4 / D57).
                    <InlineErrorState message={translate('courts.error_loading')} />
                ) : (
                    <FormField
                        type="select"
                        label={translate('game.new_court')}
                        value={selectedCourtId.toString()}
                        onChange={(value) => setSelectedCourtId(Number(value))}
                        placeholder={translate('courts.select_court')}
                        options={
                            courts?.map((court) => ({
                                value: court.id.toString(),
                                label: `${court.name}${court.id === game.court.id ? ` (${translate('game.current')})` : ''}`,
                            })) ?? []
                        }
                    />
                )}
            </FormStack>
        </FormModal>
    );
};
